/**
 * Power-bar → screen filter channel.
 *
 * Phase 1 of unifying search into the global Ask Avery "power bar": instead of
 * each data screen carrying its own keyword box, the power bar broadcasts what
 * the operator types and the active screen folds it into its existing in-memory
 * filter. Implemented with a window CustomEvent to match the app's established
 * escape hatches (`parkify:avery:ask`, `parkify:navigate`) — no shared store or
 * prop-threading needed to reach the per-screen filter state.
 *
 * Only the screens listed in FILTERABLE_PATHS take part. They filter rows that
 * are already loaded client-side (Zones/Permits/Tenants/Disputes/Transactions),
 * so feeding the query straight into their `search` state is instant and safe.
 * Customers (server-backed) and Home's global SearchBar are deliberately out of
 * scope here — see Phase 2.
 */
import { useEffect } from 'react'

export const FILTER_EVENT = 'parkify:filter'

// URL path → filter key, for the screens whose list supports the power-bar
// filter. The key is the string each screen passes to useScreenFilter(). Keep
// the paths in sync with SCREEN_PATHS in App.tsx.
export const FILTERABLE_PATHS: Record<string, string> = {
  '/transactions': 'transactions',
  '/zones':        'zones',
  '/permits':      'permits',
  '/tenants':      'tenants',
  '/validations':  'validations',
  '/disputes':     'disputes',
  // Customers is server-backed (CS API): the screen debounce-loads on filter
  // change rather than filtering an in-memory list, but from the bar's side it's
  // a filterable screen like any other.
  '/customers':    'customers',
}

// Human label for the context-aware power-bar placeholder ("Filter <label>…").
export const FILTER_LABELS: Record<string, string> = {
  transactions: 'transactions',
  zones:        'zones',
  permits:      'permits',
  tenants:      'tenants',
  validations:  'validations',
  disputes:     'disputes',
  customers:    'customers',
}

export interface FilterDetail { screen: string; query: string }

// Last non-consumed filter per screen. Lets a pre-filter set the instant before
// the destination mounts (clicking a global-search result navigates, then
// immediately calls setScreenFilter) survive the mount race: the live event is
// missed because the target screen isn't subscribed yet, so it reads this on mount.
const pendingFilter: Record<string, string> = {}

/** Resolve a router pathname to its filter key, or null if the screen opts out. */
export function filterKeyForPath(pathname: string): string | null {
  return FILTERABLE_PATHS[pathname] ?? null
}

/** Broadcast the current power-bar text to whichever screen owns `screen`. */
export function setScreenFilter(screen: string, query: string): void {
  pendingFilter[screen] = query
  window.dispatchEvent(new CustomEvent<FilterDetail>(FILTER_EVENT, { detail: { screen, query } }))
}

/**
 * Screens call this to receive the power bar's text into their existing search
 * state. `setQuery` should be a stable setter (a useState setter is fine).
 * On mount it consumes any pending filter (set just before navigation), then
 * listens for live updates while mounted.
 */
export function useScreenFilter(screen: string, setQuery: (q: string) => void): void {
  useEffect(() => {
    // Consume a filter set right before this screen mounted (global-search
    // click-through). Consume-once: delete so a later remount doesn't re-apply.
    const queued = pendingFilter[screen]
    if (queued) { setQuery(queued); delete pendingFilter[screen] }
    const onFilter = (e: Event) => {
      const detail = (e as CustomEvent<FilterDetail>).detail
      if (detail?.screen === screen) setQuery(detail.query)
    }
    window.addEventListener(FILTER_EVENT, onFilter)
    return () => window.removeEventListener(FILTER_EVENT, onFilter)
  }, [screen, setQuery])
}
